/**
 * Hotspot region geometry. Coordinates are percentages of the image (0-100) and mirror
 * HotspotRegion in assessment/questions/hotspot.py, which does the grading:
 *   rect: [x, y, width, height]   ellipse: [cx, cy, rx, ry]   polygon: [x1, y1, x2, y2, ...]
 * When regions overlap, the last one (drawn on top) wins.
 */

export const SHAPES = ['rect', 'ellipse', 'polygon']

export const clampPercent = value => Math.min(100, Math.max(0, value))

export function regionContains(region, x, y) {
  const c = region.coords || []
  if (region.shape === 'rect' && c.length === 4) {
    return x >= c[0] && x <= c[0] + c[2] && y >= c[1] && y <= c[1] + c[3]
  }
  if (region.shape === 'ellipse' && c.length === 4 && c[2] > 0 && c[3] > 0) {
    return ((x - c[0]) / c[2]) ** 2 + ((y - c[1]) / c[3]) ** 2 <= 1
  }
  if (region.shape === 'polygon' && c.length >= 6) {
    // ray casting
    let inside = false
    const n = c.length / 2
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const [xi, yi, xj, yj] = [c[i * 2], c[i * 2 + 1], c[j * 2], c[j * 2 + 1]]
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside
      }
    }
    return inside
  }
  return false
}

/** The region a point lands in, or null. Regions without shapes (pre-feedback) never match. */
export function hitRegion(regions, x, y) {
  let hit = null
  for (const region of regions) {
    if (regionContains(region, x, y)) hit = region
  }
  return hit
}

/** Maps each selection ({x, y} or {region}) to the region it lands in, or null. */
export function resolveSelections(regions, selections) {
  const byGuid = Object.fromEntries(regions.map(r => [r.guid, r]))
  return selections.map(sel =>
    sel.region !== undefined ? byGuid[sel.region] || null : hitRegion(regions, sel.x, sel.y)
  )
}

/** Turns a drag from start to end into rect or ellipse coords. */
export function coordsFromDrag(shape, start, end) {
  const left = Math.min(start.x, end.x)
  const top = Math.min(start.y, end.y)
  const width = Math.abs(end.x - start.x)
  const height = Math.abs(end.y - start.y)
  if (shape === 'ellipse') {
    return [left + width / 2, top + height / 2, width / 2, height / 2]
  }
  return [left, top, width, height]
}

/** The region's bounding box as { minX, minY, maxX, maxY }. */
export function regionBounds(region) {
  const c = region.coords || []
  if (region.shape === 'rect') return { minX: c[0], minY: c[1], maxX: c[0] + c[2], maxY: c[1] + c[3] }
  if (region.shape === 'ellipse') return { minX: c[0] - c[2], minY: c[1] - c[3], maxX: c[0] + c[2], maxY: c[1] + c[3] }
  const xs = c.filter((_, i) => i % 2 === 0)
  const ys = c.filter((_, i) => i % 2 === 1)
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) }
}

/**
 * Where a region's label goes: centered just above the shape, so it doesn't cover pins inside
 * it, or just below when the shape touches the top of the image.
 */
export function labelAnchor(region) {
  const b = regionBounds(region)
  const x = (b.minX + b.maxX) / 2
  return b.minY < 8 ? { x, y: b.maxY, below: true } : { x, y: b.minY, below: false }
}

/** Rounds to two decimals so saved coords stay short. */
export const round2 = value => Math.round(value * 100) / 100

/** Pointer position within an element as image percentages. */
export function pointerPercent(event, element) {
  const rect = element.getBoundingClientRect()
  return {
    x: round2(clampPercent(((event.clientX - rect.left) / rect.width) * 100)),
    y: round2(clampPercent(((event.clientY - rect.top) / rect.height) * 100)),
  }
}
